import { ApiErrorData, ApiResponse } from 'models/ApiResponse';
import {
  AvailableLeadsParams,
  AvailableLeadsResponse,
  CancelOperationRequestInput,
  CreateOperationRequestInput,
  OperationAssignmentContext,
  OperationRequest,
  OperationRequestDraftResult,
  OperationRequestListParams,
  OperationRequestListResponse,
  OperationRequestPayload,
  OperationRequestReceipt,
  OperationRequestReceiptEmail,
  OperationSupervision,
  PublishAssignmentInput,
  PublishAssignmentResult,
  PublishPreview,
  SaveAssignmentInput,
  SaveDraftInput,
} from 'models/OperationRequest';

import { FunctionsHelper } from 'utils/FunctionsHelper';
import MainBackendAxios from 'utils/MainBackendAxios';

const BASE = '/portal-go/operation-requests';

const OPS_EMAIL_TO = 'operaciones@touch.cl';

export const pctOf = (
  r: Pick<{ filled: number; slots: number }, 'filled' | 'slots'>
) => (r.slots ? Math.round((r.filled / r.slots) * 100) : 0);

const buildReceiptEmail = (params: {
  id: string;
  brand: string;
  campaign: string;
  week: string;
  kam: string;
  turnos: number;
  slots: number;
  stores: number;
}): OperationRequestReceiptEmail => ({
  to: OPS_EMAIL_TO,
  cc: params.kam,
  subject: `Nuevo requerimiento ${params.id} · ${params.brand} — ${params.campaign}`,
  body: `Hola equipo, ${params.kam} envió un requerimiento para la ${params.week}: ${params.turnos} turnos y ${params.slots} cupos en ${params.stores} tiendas. Revisa el detalle y asigna supervisor y personal en Portal GO.`,
});

const toUpsertBody = (
  payload: OperationRequestPayload,
  extras?: { filled?: number; supervisor?: string }
) => ({
  status: payload.status === 'draft' ? 'draft' : 'submitted',
  brief: payload.brief,
  plan: payload.plan,
  metrics: payload.metrics,
  ...(extras?.filled !== undefined ? { filled: extras.filled } : {}),
  ...(extras?.supervisor ? { supervisor: extras.supervisor } : {}),
});

const receiptFromRequest = (
  request: OperationRequest & { cost?: number }
): OperationRequestReceipt => ({
  id: request.id,
  brand: request.brand,
  type: request.type,
  campaign: request.campaign,
  week: request.week,
  profile: request.profile,
  kam: request.kam,
  turnos: request.turnos,
  slots: request.slots,
  stores: request.stores,
  vipStores: request.vipStores,
  cost: Number(request.cost) || 0,
  email: buildReceiptEmail({
    id: request.id,
    brand: request.brand,
    campaign: request.campaign,
    week: request.week,
    kam: request.kam,
    turnos: request.turnos,
    slots: request.slots,
    stores: request.stores,
  }),
});

const detailToPayload = (detail: any): OperationRequestPayload => {
  const brief = detail.brief || {};
  const plan = detail.plan || { storeIds: [], cells: {}, turns: [] };
  const isDraft = detail.status === 'borrador';
  return {
    status: isDraft ? 'draft' : 'submitted',
    brief: {
      clientId: detail.clientId ?? brief.clientId ?? null,
      campaignId: detail.campaignId ?? brief.campaignId ?? null,
      client: brief.client || detail.brand || '',
      type: brief.type || detail.type || 'contrato',
      campaign: brief.campaign || detail.campaign || '',
      week: brief.week || detail.week || '',
      profile: brief.profile || detail.profile || '',
      uniform: brief.uniform || detail.uniform || '',
      goal: brief.goal || detail.goal || '',
      description: brief.description || detail.description || '',
      kam: brief.kam || detail.kam || '',
      rate: Number(brief.rate ?? detail.rate) || 0,
      blocks: brief.blocks || {
        am: { from: '10:00', to: '14:00' },
        pm: { from: '14:00', to: '18:00' },
        full: { from: '10:00', to: '18:00' },
      },
    },
    plan: {
      storeIds: plan.storeIds || [],
      cells: plan.cells || {},
      turns: plan.turns || [],
    },
    metrics: {
      turnos: Number(detail.turnos) || 0,
      people: Number(detail.slots) || 0,
      hours: Number(detail.hours) || 0,
      cost: Number(detail.cost) || 0,
    },
  };
};

export const OperationRequirementService = {
  list: async (
    params: OperationRequestListParams = {}
  ): Promise<ApiResponse<OperationRequestListResponse | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.get(`${BASE}/list`, {
        params: {
          term: params.term || undefined,
          tab: params.tab || 'todas',
          page: params.page || 1,
          pageSize: params.pageSize || 10,
          sortKey: params.sortKey || 'startsIn',
          sortDir: params.sortDir || 'asc',
        },
      });

      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  getById: async (
    id: string
  ): Promise<ApiResponse<OperationRequest | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.get(
        `${BASE}/find-by-id/${encodeURIComponent(id)}`
      );
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  getDraft: async (
    id: string
  ): Promise<ApiResponse<OperationRequestDraftResult | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.get(
        `${BASE}/find-by-id/${encodeURIComponent(id)}`
      );
      const detail = response?.data?.data;
      return {
        success: true,
        status: response.status,
        data: {
          id: detail.id,
          status: 'draft',
          requestStatus: detail.status,
          savedAt: detail.updatedAt || detail.createdAt,
          payload: detailToPayload(detail),
        },
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  create: async (
    input: CreateOperationRequestInput
  ): Promise<
    ApiResponse<
      | { request: OperationRequest; receipt: OperationRequestReceipt }
      | ApiErrorData
    >
  > => {
    try {
      const payload =
        input.payload ||
        ({
          status: 'submitted',
          brief: input.brief,
          plan: {
            storeIds: [
              ...new Set(
                input.turns
                  .map((t) => t.storeId)
                  .filter((id): id is number => !!id)
              ),
            ],
            cells: {},
            turns: input.turns,
          },
          metrics: {
            turnos: input.turns.length,
            people: input.turns.reduce((s, t) => s + Number(t.people), 0),
            hours: 0,
            cost: 0,
          },
        } as OperationRequestPayload);

      const body = toUpsertBody({ ...payload, status: 'submitted' });
      const response = input.draftId
        ? await MainBackendAxios.put(
            `${BASE}/update/${encodeURIComponent(input.draftId)}`,
            body
          )
        : await MainBackendAxios.post(`${BASE}/create`, body);
      const request = response?.data?.data as OperationRequest & {
        cost?: number;
      };

      return {
        success: true,
        status: response.status,
        data: {
          request,
          receipt: receiptFromRequest(request),
        },
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  saveDraft: async (
    input: SaveDraftInput
  ): Promise<ApiResponse<OperationRequestDraftResult | ApiErrorData>> => {
    try {
      const body = toUpsertBody({ ...input.payload, status: 'draft' });
      const response = input.draftId
        ? await MainBackendAxios.put(
            `${BASE}/update/${encodeURIComponent(input.draftId)}`,
            body
          )
        : await MainBackendAxios.post(`${BASE}/create`, body);

      const detail = response?.data?.data;
      return {
        success: true,
        status: response.status,
        data: {
          id: detail.id,
          status: 'draft',
          savedAt:
            detail.updatedAt || detail.createdAt || new Date().toISOString(),
          payload: detailToPayload(detail),
        },
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  deleteDraft: async (
    id: string
  ): Promise<ApiResponse<{ id: string } | ApiErrorData>> => {
    try {
      await MainBackendAxios.delete(`${BASE}/delete/${encodeURIComponent(id)}`);
      return { success: true, status: 200, data: { id } };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  /** Actualiza un requerimiento ya enviado (preserva filled en BE). */
  update: async (
    id: string,
    input: { payload: OperationRequestPayload }
  ): Promise<
    ApiResponse<
      | { request: OperationRequest; receipt: OperationRequestReceipt }
      | ApiErrorData
    >
  > => {
    try {
      const response = await MainBackendAxios.put(
        `${BASE}/update/${encodeURIComponent(id)}`,
        toUpsertBody({ ...input.payload, status: 'submitted' })
      );
      const request = response?.data?.data as OperationRequest & {
        cost?: number;
      };
      return {
        success: true,
        status: response.status,
        data: {
          request,
          receipt: receiptFromRequest(request),
        },
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  /** Actualiza Asignación (personas filled / cupos slots). */
  setAssignment: async (
    id: string,
    filled: number
  ): Promise<ApiResponse<OperationRequest | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.put(
        `${BASE}/set-assignment/${encodeURIComponent(id)}`,
        { filled }
      );
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  reassignSupervisor: async (
    id: string,
    supervisor: string
  ): Promise<ApiResponse<OperationRequest | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.put(
        `${BASE}/set-supervisor/${encodeURIComponent(id)}`,
        { supervisor }
      );
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  /** Requerimiento + salas del plan + supervisores + reparto guardado. */
  getAvailableLeads: async (
    params: AvailableLeadsParams
  ): Promise<ApiResponse<AvailableLeadsResponse | ApiErrorData>> => {
    try {
      const query: Record<string, unknown> = {
        country: params.country,
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 50,
      };
      if (params.q && params.q.trim()) query.q = params.q.trim();
      if (params.regionIds?.length) query.regionIds = params.regionIds;
      if (params.cityIds?.length) {
        query.cityIds = params.cityIds.map((id) => String(id));
      }
      if (params.scoreProfile?.length) {
        query.scoreProfile = params.scoreProfile.map((n) => String(n));
      }
      if (params.tagIds?.length) {
        query.tagIds = params.tagIds.map((id) => String(id));
      }
      const response = await MainBackendAxios.get(`${BASE}/available-leads`, {
        params: query,
        paramsSerializer: { indexes: null },
      });
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  getAssignmentContext: async (
    id: string
  ): Promise<ApiResponse<OperationAssignmentContext | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.get(
        `${BASE}/assignment-context/${encodeURIComponent(id)}`
      );
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  setSupervision: async (
    id: string,
    supervision: OperationSupervision
  ): Promise<ApiResponse<OperationRequest | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.put(
        `${BASE}/set-supervision/${encodeURIComponent(id)}`,
        { sups: supervision.sups, map: supervision.map }
      );
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  /** Guarda la propuesta de asignación; con `publish` la marca publicada. */
  saveAssignment: async (
    id: string,
    input: SaveAssignmentInput
  ): Promise<ApiResponse<OperationRequest | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.put(
        `${BASE}/save-assignment/${encodeURIComponent(id)}`,
        { slots: input.slots, forced: input.forced, publish: input.publish }
      );
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  /** Destinatarios, emails y turnos que se notificarán al publicar. */
  getPublishPreview: async (
    id: string,
    input: PublishAssignmentInput
  ): Promise<ApiResponse<PublishPreview | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.post(
        `${BASE}/publish-preview/${encodeURIComponent(id)}`,
        { slots: input.slots, forced: input.forced }
      );
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  /** Publica la asignación y envía un correo a cada persona. */
  publishAssignment: async (
    id: string,
    input: PublishAssignmentInput
  ): Promise<ApiResponse<PublishAssignmentResult | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.put(
        `${BASE}/publish-assignment/${encodeURIComponent(id)}`,
        { slots: input.slots, forced: input.forced }
      );
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  cancel: async (
    id: string,
    body: CancelOperationRequestInput
  ): Promise<ApiResponse<OperationRequest | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.put(
        `${BASE}/cancel/${encodeURIComponent(id)}`,
        {
          reason: body.reason,
          detail: body.detail || null,
        }
      );
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },
};
